import type { ReactNode } from "react";
import { ApiError } from "../../lib/api";
import { cn } from "../../lib/utils";

// Shared table + form primitives for the EEX pages. These were duplicated
// across eex.trades.tsx and eex.margin.tsx; this is their single home.

export function Th({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return <th colSpan={colSpan} className={cn("px-2 py-1.5 font-normal whitespace-nowrap text-left", className)}>{children}</th>;
}

export function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return <td colSpan={colSpan} className={cn("px-2 py-1 whitespace-nowrap", className)}>{children}</td>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5 text-[10px] text-muted uppercase tracking-wide">
      {label}
      {children}
    </label>
  );
}

export function WarningList({ warnings }: { warnings: string[] }) {
  return (
    <details className="text-muted text-xs">
      <summary className="min-h-11 cursor-pointer touch-manipulation py-3 sm:min-h-0 sm:py-0">
        {warnings.length} warning(s)
      </summary>
      <ul className="pl-4 list-disc mt-1 space-y-0.5">
        {warnings.slice(0, 50).map((w, i) => (
          <li key={i}>{w}</li>
        ))}
        {warnings.length > 50 && <li className="italic">…and {warnings.length - 50} more</li>}
      </ul>
    </details>
  );
}

export function StatusBadge({ status }: { status: "pending" | "confirmed" }) {
  return (
    <span
      className={cn(
        "px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide",
        status === "confirmed"
          ? "bg-posGreen/15 text-posGreen"
          : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
      )}
    >
      {status}
    </span>
  );
}

export function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return (e as Error).message;
}

// ── Recognition edit-gate confirm flow (Phase 4) ─────────────────────────────
//
// A mutating trade call may answer 409 { code: "confirm_required", impact }
// when it moves already-recognized profit (WARNING tier): show the per-cell
// impact, collect a mandatory reason, and resubmit with
// { confirmImpact: true, reason }. A 423 { code: "locked" } is final — booked
// months change only via restatement — and surfaces as a normal error.
export interface RecognitionImpact {
  reasons: string[];
  affectedCells: {
    month: string;
    book: string | null;
    trader: string | null;
    delta: number;
  }[];
}

export interface GateExtras {
  reason?: string;
  confirmImpact?: boolean;
}

export async function withRecognitionConfirm<T>(
  run: (extras: GateExtras) => Promise<T>
): Promise<T | null> {
  try {
    return await run({});
  } catch (e) {
    const body = e instanceof ApiError ? (e.body as { code?: string; impact?: RecognitionImpact } | undefined) : undefined;
    if (body?.code !== "confirm_required" || !body.impact) throw e;
    const cells = body.impact.affectedCells
      .map(
        (c) =>
          `${c.month}  ${c.book ?? "—"} · ${c.trader ?? "—"}:  ${c.delta >= 0 ? "+" : ""}${Math.round(c.delta).toLocaleString("en-US")} EUR`
      )
      .join("\n");
    // Only an edit that relocates a close into a DIFFERENT month actually moves
    // profit "between months". A same-month move (re-booking, or touching a
    // closed month) reassigns WITHIN the month — don't claim otherwise.
    const months = new Set(body.impact.affectedCells.map((c) => c.month));
    const headline =
      months.size > 1
        ? "This change moves recognized profit between financial months:"
        : months.size === 1
          ? `This change reassigns recognized profit within ${[...months][0]}:`
          : "This change affects recognized profit:";
    const ok = window.confirm(`${headline}\n\n${cells}\n\n${body.impact.reasons.join("\n")}\n\nProceed?`);
    if (!ok) return null;
    const reason = window.prompt("Reason for this change (required — recorded in the audit trail):");
    if (!reason?.trim()) return null;
    return run({ reason: reason.trim(), confirmImpact: true });
  }
}
